import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { removeSnack } from 'snackbar/snackbarSlice';
import { GridCloseIcon } from '@mui/x-data-grid';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import palette from 'library/theme/palette';

let displayed = [];

const Snackbar = () => {
  const dispatch = useDispatch();
  const list = useSelector(store => store.snackbar.list || []);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const storeDisplayed = id => {
    displayed = [...displayed, id];
  };

  const removeDisplayed = id => {
    displayed = [...displayed.filter(key => id !== key)];
  };

  useEffect(() => {
    list.forEach(({ key, message, options = {}, dismissed = false, title }) => {
      if (dismissed) {
        // dismiss snackbar using notistack
        closeSnackbar(key);
        return;
      }

      // do nothing if snackbar is already displayed
      if (displayed.includes(key)) return;

      // if persistent without an action, add close button
      const opts = {
        ...options,
        action:
          options.persist && !options.action
            ? k => (
                <Box color="background.paper">
                  <Button
                    color="inherit"
                    onClick={() => closeSnackbar(k)}
                    size="small"
                    sx={{
                      '&:hover': {
                        color: 'text.primary',
                      },
                      fontFamily: 'Gothic A1',
                      fontSize: 14,
                    }}
                    variant="text"
                  >
                    CLOSE
                  </Button>
                </Box>
              )
            : k => (
                <>
                  {options.action}
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    onClick={() => closeSnackbar(k)}
                    size="small"
                  >
                    <GridCloseIcon fontSize="small" />
                  </IconButton>
                </>
              ),
      };

      if (title) {
        const isError = options?.variant === 'error';
        const backgroundColor = isError ? palette._next.alert.light : palette._next.green.light;
        const iconColor = isError ? palette._next.error.main : palette._next.green.main;
        const textColor = isError ? palette._next.alert.dark : palette._next.green.dark;

        enqueueSnackbar('', {
          key,
          ...opts,
          content: snackbarKey => (
            <Box
              sx={{
                alignItems: 'flex-start',
                backgroundColor,
                borderRadius: 1,
                boxShadow: 6,
                color: textColor,
                display: 'flex',
                height: 'auto',
                maxWidth: 320,
                padding: 2,
              }}
            >
              {isError ? (
                <ErrorOutlineIcon sx={{ color: iconColor, mr: 1.5, mt: '2px' }} />
              ) : (
                <CheckCircleOutlineIcon sx={{ color: iconColor, mr: 1.5, mt: '2px' }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    fontFamily: 'Overpass',
                    fontSize: 16,
                    fontWeight: '700',
                    lineHeight: 1.43,
                    mb: 1,
                    mr: 0.5,
                    mt: 0.5,
                  }}
                >
                  {title}
                </Box>
                <Box
                  sx={{ fontFamily: 'Overpass', fontSize: 14, fontWeight: '500', lineHeight: 1.35 }}
                >
                  {message}
                </Box>
              </Box>
              <IconButton
                aria-label="close"
                onClick={() => closeSnackbar(snackbarKey)}
                size="small"
                sx={{ color: textColor, ml: 1 }}
              >
                <GridCloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ),
          onClose: (event, reason, myKey) => {
            if (opts.onClose) {
              opts.onClose(event, reason, myKey);
            }
          },
          onExited: (event, myKey) => {
            dispatch(removeSnack(myKey));
            removeDisplayed(myKey);
          },
        });
      } else {
        enqueueSnackbar(message, {
          key,
          ...opts,
          onClose: (event, reason, myKey) => {
            if (opts.onClose) {
              opts.onClose(event, reason, myKey);
            }
          },
          onExited: (event, myKey) => {
            // remove this snackbar from redux store
            dispatch(removeSnack(myKey));
            removeDisplayed(myKey);
          },
        });
      }

      // keep track of snackbars that we've displayed
      storeDisplayed(key);
    });
  }, [list, closeSnackbar, enqueueSnackbar, dispatch]);

  return null;
};

export default Snackbar;
